'use client';
import { Popover, PopoverContent, PopoverTrigger } from '@eliadmin/core/src/components/ui/popover';
import { TOWN_ENTRIES } from '@eliadmin/core/src/constants/town-data';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { usePathname, useRouter } from 'next/navigation';
import { type PropsWithChildren, useEffect, useState } from 'react';
import { resolveLocalizedHref } from '@eliadmin/core/src/components/link/prefetch-link';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import type { Menu } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export type CategoryMenuProps = {
  className?: string;
};

export function CategoryMenu(props: PropsWithChildren<CategoryMenuProps>) {
  return (
    <ul className={cn('flex flex-row items-center gap-2', 'ea-category-menu', props.className)}>
      {props.children}
    </ul>
  );
}

export type CategoryMenuItemProps = {
  title: string;
  href?: string;
  subpages?: Menu['fields'];
  popover?: boolean;
  onClick?: () => void;
  order?: number;
  className?: {
    root?: string;
    link?: string;
  };
};

export function CategoryMenuItem(props: CategoryMenuItemProps) {
  const { t } = useTranslations();
  const LinkComp = props.href ? (isRelativeHref(getSafeHref(props.href)) ? Link : 'a') : 'span';
  const currentUrl = usePathname();
  const safeHref = getSafeHref(props.href ?? '');
  const normalizedHref = safeHref.replace(/^\/+|\/+$/g, '');
  const isCitiesMenu = normalizedHref === 'mesta' || normalizedHref.endsWith('/mesta');
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const currentLocale = useLocaleStore(state => state.currentLocale);
  const defaultLocale = useLocaleStore(state => state.defaultLocale);
  const supportedLocales = useLocaleStore(state => state.supportedLocales);

  useEffect(() => {
    // Popover contents are unmounted while closed. Warm their routes explicitly,
    // instead of waiting for city links to become visible or hovered.
    const targets = isCitiesMenu
      ? ['/mesta', ...TOWN_ENTRIES.map(town => `/${town.slug}`)]
      : [safeHref];
    for (const target of new Set(targets)) {
      if (!target || target.startsWith('#') || target.startsWith('?')) continue;
      const localized = resolveLocalizedHref(target, {
        currentLocale,
        defaultLocale,
        locales: supportedLocales.map(locale => locale.locale),
      });
      if (typeof localized !== 'string') continue;
      const url = new URL(localized, window.location.origin);
      if (url.origin !== window.location.origin) continue;
      router.prefetch(`${url.pathname}${url.search}`);
    }
  }, [router, safeHref, isCitiesMenu, currentLocale, defaultLocale, supportedLocales]);

  useEffect(() => setOpen(false), [currentUrl]);
  const closeMenu = () => {
    setOpen(false);
    props.onClick?.();
  };

  const cities = TOWN_ENTRIES.map(town => ({
    name: `${t('text4118')} ${town.name}`,
    slug: town.slug,
  }));

  return (
    <>
      {isCitiesMenu ? (
        <li className="contents">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger
              className={cn(
                'relative text-sm font-medium',
                cities.some(city => currentUrl === `/${city.slug}`) ? 'text-primary' : 'text-white',
              )}
            >
              {props.title}
              {cities.some(city => currentUrl === `/${city.slug}`) && (
                <div className="absolute bottom-[-2px] left-0 h-[2px] w-full bg-primary"></div>
              )}
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="flex max-w-[300px] flex-col items-start justify-start gap-2 rounded-none border-none bg-black/80 p-4"
            >
              {cities.map(city => (
                <Link
                  prefetch
                  onClick={closeMenu}
                  href={`/${city.slug}`}
                  key={city.slug}
                  className="text-sm font-medium text-white"
                >
                  {city.name}
                </Link>
              ))}
              <Link
                prefetch
                onClick={closeMenu}
                href="/mesta"
                className="mt-2 border-t border-white/20 pt-3 text-sm font-bold text-primary"
              >
                {t('text4117')}
              </Link>
            </PopoverContent>
          </Popover>
        </li>
      ) : (
        <li
          style={{ order: props.order }}
          className={cn(
            'flex flex-row',
            {
              'cursor-pointer': !!props.href,
              'cursor-default': !props.href,
            },
            'ea-category-menu-item',
            props.className?.root,
          )}
        >
          <LinkComp
            href={props.href ? getSafeHref(props.href) : (undefined as any)}
            className={cn(
              'relative text-sm font-medium text-white',
              'ea-category-menu-item__link',
              props.className?.link,
              getSafeHref(props.href!) === currentUrl ? 'text-primary' : 'text-white',
            )}
            onClick={props.onClick}
          >
            {props.title}
            {getSafeHref(props.href!) === currentUrl && (
              <div className="absolute bottom-[-2px] left-0 h-[2px] w-full bg-primary"></div>
            )}
          </LinkComp>
        </li>
      )}
    </>
  );
}
